<template>
  <div ref="ETABLISSEMENT_LIST" class="col-6">
    <div v-for="(item, index) in items" :key="index" class="pb-3">
      <div @click="selectItem(item)" class="list-card">
        <v-row class="ma-0">
          <div :class="item.logo ? 'list-logo' : 'list-logo list-logo-initial'">
            <img v-if="item.logo" :src="item.logo" width="100%" />
            <div v-else>{{ getSocInitials(item) }}</div>
          </div>
          <div style="padding-left: 12px;">
            <div class="card-title">
              {{ getSocName(item) }}
            </div>
            <div class="card-sub-title">
              {{ getPaymentInfo(item) }}
            </div>
            <div v-if="getBillInfo(item) != ''" class="card-sub-title">
              {{ getBillInfo(item) }}
            </div>
          </div>
        </v-row>

        <div class="seperator"/>

        <v-row class="ma-0">
          <div class="info-box">
            <div class="info-header">Abonnement</div>
            <div class="info-text">
              {{ getSubscriptionType(item) }}
            </div>
          </div>

          <div class="info-box">
            <div class="info-header">Coût mensuel</div>
            <div class="info-text">{{ getSubscriptionCost(item) }}</div>
          </div>

          <div class="info-box">
            <div class="info-header">Date de souscription</div>
            <div class="info-text">{{ getSubscriptionDate(item) }}</div>
          </div>

          <div class="info-box">
            <div class="info-header">Nombre de paies</div>
            <div class="info-text">
              {{ getSubscriptionPaies(item) }}
              <v-icon v-if="item.trancheEffectifLabel" class="pl-2" color="rgb(var(--v-theme-secondary60))" size="14" @click.stop="copyText(item.trancheEffectifLabel)">fas fa-copy</v-icon>
            </div>
            <div>

            </div>
          </div>
        </v-row>
        <v-row v-if="nextAbo(item)"
               class="ma-0">
          <div class="info-box">
            <div class="info-header">Modification abonnement</div>
            <div class="info-text">
              {{ nextAboLabel(item) }}
            </div>
          </div>
        </v-row>
        <v-row v-if="negoFrais(item)"
               class="ma-0">
          <div class="info-box">
            <div class="info-header">Frais de traitement négociés</div>
            <div class="info-text">
              {{ negoFraisLabel(item) }}
            </div>
          </div>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import SectionAbonnementList from '@/generated/sections/SectionAbonnementList.vue';
  import SearchFilterInput from "@/framework/base/components/SearchFilterInput.vue";
  import type Subscription from "@/generated/classes/Subscription";
  import { storeEtablissement, storeSubscriptionType, storeSubscription, storePaymentMeans, storeContrat, storePaie } from "@/generated/stores";
  import { getCurrency } from '@/framework/base/helpers/Localization';
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import Owner from "@/generated/classes/Owner";
  import SubscriptionType from '@/extend/classes/SubscriptionType';
  import { ListFilter } from '@/generated/classes/ListData';
  import { PaieStatuts } from "@/generated/classes/Paie";

  export default defineComponent({
    components: { SearchFilterInput },
    extends: SectionAbonnementList,

    created () {
      const allData = new ListFilter();
      allData.pageLimit = -1;
      storeSubscription.listFilteredSubscription(allData);
      storeEtablissement.listFilteredEtablissement(allData);
      storeContrat.listFilteredContrat(allData);
      storePaie.listFilteredPaie(allData);
    },

    computed: {
      items (): Subscription[] {
        if (!this.isInitialised) {
          this.defaultFilter();
          this.loadTable();
          this.isInitialised = true;
        }
        return this.listData.reccords.sort((a, b) => a.id - b.id);
      },
    },
    methods: {
      getSocInitials (sub: Subscription): string {
        const etab = storeEtablissement.getEtablissement(sub.ownerId);
        const name = etab.alias;
        const initials = name.split(/[ -.]+/);
        if (initials.length > 1) {
          return initials[0][0] + initials[initials.length - 1][0];
        } else {
          return initials[0][0] + initials[0][1];
        }
      },
      getSocName (sub: Subscription): string {
        const etab = storeEtablissement.getEtablissement(sub.ownerId);
        return (etab.alias);
      },
      getPaymentInfo (sub: Subscription): string {
        let res = ''
        if (sub.aboMeansId > 0) {
          const paymentMeans = storePaymentMeans.getPaymentMeans(sub.aboMeansId);
          if (paymentMeans) {
            res += paymentMeans.label;
          }
        }
        return res;
      },
      getBillInfo (sub: Subscription): string {
        let res = ''
        const etab = storeEtablissement.getEtablissement(sub.ownerId);
        const etabs = storeEtablissement.listEtablissement.filter(a => a.clientId == etab.clientId && a.societeId == etab.societeId);
        if (etabs.length > 1) {
          res += ' facturé à ' + etab.alias;
        }
        return res;
      },
      getSubscriptionType (sub: Subscription): string {
        const subType = storeSubscriptionType.getSubscriptionType(sub.subscriptionTypeId);
        return subType.label;
      },
      getSubscriptionCost (sub: Subscription): string {
        let res = '';
        const subType = storeSubscriptionType.getSubscriptionType(sub.subscriptionTypeId);
        if (sub.mensuel) {
          res = getCurrency(sub.mensuel)
        } else {
          res = getCurrency(subType.mensuel);
        }
        return res;
      },
      getSubscriptionDate (sub: Subscription): string {
        return DateFormat.toEdit(sub.dateDebut);
      },
      getSubscriptionPaies (sub: Subscription): string {
        const subType = storeSubscriptionType.getSubscriptionType(sub.subscriptionTypeId);       
        let res = sub.nbPaiesUsed;
        if (subType.maxPaies > 0) {
          res += ' sur ' + subType.maxPaies + ' max';
        } else {
          res += ' (illimité)';
        }
        return res;
      },
      selectItem (sub: Subscription): void {
        const owner = new Owner();
        owner.objId = sub.ownerId;
        owner.objType = sub.ownerType;
        SubscriptionType.change(owner);
      },
      nextAbo (sub: Subscription): boolean {
        return sub.nextTypeId > 0;
      },
      nextAboLabel (sub: Subscription): string {
        const nextSubscriptionType = storeSubscriptionType.getSubscriptionType(sub.nextTypeId);
        return 'Abonnement ' + nextSubscriptionType.label + ' à partir du ' + DateFormat.toEdit(sub.dateUpdate);
      },
      negoFrais (sub: Subscription): boolean {
        const hasFrais = sub.tauxTraitement > 0 || sub.minTraitement > 0 || sub.maxTraitement > 0;
        if (!hasFrais) return false;
        const today = new Date();
        const nego = (sub.debutRemise == null || new Date(sub.debutRemise) <= today) &&
                     (sub.finRemise == null || new Date(sub.finRemise) >= today);
        return nego;
      },
      negoFraisLabel (sub: Subscription): string {
        const subType = storeSubscriptionType.getSubscriptionType(sub.subscriptionTypeId);
        const taux = sub.tauxTraitement > 0 ? sub.tauxTraitement : subType.tauxTraitement;
        const min = sub.minTraitement > 0 ? sub.minTraitement : subType.minTraitement;
        const max = sub.maxTraitement > 0 ? sub.maxTraitement : subType.maxTraitement;
        return 'taux = ' + taux + '%, min = ' + min + ' € , max = ' + max + ' €';
      }
    }
  })
</script>

<style scoped>
  .logo {
    height: 50px;
    width: 50px;
    border-radius: 50%;
    border: 2px solid rgb(var(--v-theme-secondary80));
    align-content: center;
  }

  .initial {
    text-align: center;
    background-color: #CD9EF7;
    font-size: 16px;
    color: rgb(var(--v-theme-primary100));
    font-weight: 500;
    line-height: 24px;
  }

  .bottom-center {
    position: fixed;
    bottom: 15px;
    left: 50%;
    width: 150px;
    transform: translateX(-50%); /* Center horizontally */
    background-color: rgba(0, 0, 0, 0.5);
    padding: 10px;
    color: white;
    text-align: center;
  }
</style>